import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Download, ImageIcon, Plus } from 'lucide-react'
import * as XLSX from 'xlsx'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { FilterBar } from '@/components/table/FilterBar'
import { DataTable, type DataTableColumn } from '@/components/table/DataTable'
import { Pagination } from '@/components/table/Pagination'
import { MultiSelectChips } from '@/components/forms/MultiSelectChips'
import { Typeahead } from '@/components/forms/Typeahead'
import { ImageHoverPreview } from '@/components/ui/ImageHoverPreview'
import { catalogService, type CatalogItem } from '@/services/catalog'
import { listCustomersApi } from '@/services/customers/apiClient'
import type { Customer } from '@/services/customers/types'
import {
  formatAttributeNames,
  formatDimensionInch,
  PRODUCT_STATUS_OPTIONS,
  productService,
  roundMmUp,
  statusLabel,
  type Product,
  type ProductStatus,
} from '@/services/products'

export type CustomerFilterOption = {
  id: string
  code: string
  name: string
  status?: string
}

export const ALL_CUSTOMERS_OPTION: CustomerFilterOption = {
  id: 'all',
  code: 'ALL',
  name: 'Tất cả khách hàng',
}

export function buildProductExportRows(products: Product[], rowNumberOffset = 0) {
  return products.map((p, index) => ({
    STT: rowNumberOffset + index + 1,
    'Khách hàng': p.customer ? `${p.customer.code} — ${p.customer.name}` : '—',
    'Mã hàng': p.code,
    'Tên hàng': p.name,
    'Màu': formatAttributeNames(p.colors),
    'Gỗ': formatAttributeNames(p.woods),
    'Veneer': formatAttributeNames(p.veneers),
    'W': formatDimensionInch(p.w) || 0,
    'D': formatDimensionInch(p.d) || 0,
    'H': formatDimensionInch(p.h) || 0,
    'Trạng thái': statusLabel(p.status),
    'Ghi chú': p.note || '',
  }))
}

export function getProductColumns(): DataTableColumn<Product>[] {
  return [
    {
      id: 'customer',
      header: 'Khách hàng',
      width: '180px',
      cell: (row) => {
        if (!row.customer) {
          return <span className="text-slate-400">—</span>
        }
        return (
          <div className="flex flex-col text-xs leading-tight">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-slate-900">{row.customer.code}</span>
              {row.customer.status === 'inactive' && (
                <span className="inline-block rounded bg-red-50 px-1 py-0.5 text-xs font-medium text-red-700">
                  ngừng dùng
                </span>
              )}
            </div>
            <span className="text-slate-600 truncate" title={row.customer.name}>
              {row.customer.name}
            </span>
          </div>
        )
      },
    },
    {
      id: 'image',
      header: 'Ảnh',
      width: '56px',
      align: 'center',
      cell: (row) =>
        row.imageUrl ? (
          <ImageHoverPreview src={row.imageUrl} alt={row.name}>
            <img
              src={row.imageUrl}
              alt={row.name}
              className="mx-auto h-10 w-10 rounded-[var(--radius-sm)] object-cover"
            />
          </ImageHoverPreview>
        ) : (
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-[var(--radius-sm)] bg-slate-100 text-slate-400">
            <ImageIcon className="h-4 w-4" />
          </div>
        ),
    },
    {
      id: 'code',
      header: 'Mã hàng',
      sortable: true,
      sortKey: 'code',
      width: '120px',
      cell: (row) => (
        <Link
          to={`/products/${row.id}`}
          className="font-medium text-[var(--color-link)] hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.code}
        </Link>
      ),
    },
    {
      id: 'name',
      header: 'Tên hàng',
      sortable: true,
      sortKey: 'name',
      width: '200px',
      cell: (row) => (
        <span className="font-medium text-[var(--color-text)]">{row.name}</span>
      ),
    },
    {
      id: 'colors',
      header: 'Màu',
      width: '140px',
      cell: (row) => (
        <span className="text-xs text-slate-700">
          {formatAttributeNames(row.colors)}
        </span>
      ),
    },
    {
      id: 'woods',
      header: 'Gỗ',
      width: '120px',
      cell: (row) => (
        <span className="text-xs text-slate-700">
          {formatAttributeNames(row.woods)}
        </span>
      ),
    },
    {
      id: 'veneers',
      header: 'Veneer',
      width: '120px',
      cell: (row) => (
        <span className="text-xs text-slate-700">
          {formatAttributeNames(row.veneers)}
        </span>
      ),
    },
    {
      id: 'w',
      header: 'W',
      sortable: true,
      sortKey: 'w',
      align: 'right',
      width: '56px',
      cell: (row) => (
        <span className="tabular-nums text-slate-700" title={`${roundMmUp(row.w)} mm`}>
          {row.w ? formatDimensionInch(row.w) : '—'}
        </span>
      ),
    },
    {
      id: 'd',
      header: 'D',
      sortable: true,
      sortKey: 'd',
      align: 'right',
      width: '56px',
      cell: (row) => (
        <span className="tabular-nums text-slate-700" title={`${roundMmUp(row.d)} mm`}>
          {row.d ? formatDimensionInch(row.d) : '—'}
        </span>
      ),
    },
    {
      id: 'h',
      header: 'H',
      sortable: true,
      sortKey: 'h',
      align: 'right',
      width: '56px',
      cell: (row) => (
        <span className="tabular-nums text-slate-700" title={`${roundMmUp(row.h)} mm`}>
          {row.h ? formatDimensionInch(row.h) : '—'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Trạng thái',
      sortable: true,
      sortKey: 'status',
      width: '168px',
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'note',
      header: 'Ghi chú',
      width: '160px',
      cell: (row) => (
        <span className="line-clamp-1 text-[var(--color-text-muted)]">
          {row.note || '—'}
        </span>
      ),
    },
  ]
}

export function ProductListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'active' | ProductStatus>('active')
  const [colorIds, setColorIds] = useState<string[]>([])
  const [woodIds, setWoodIds] = useState<string[]>([])
  const [veneerIds, setVeneerIds] = useState<string[]>([])
  const [colors, setColors] = useState<CatalogItem[]>([])
  const [woods, setWoods] = useState<CatalogItem[]>([])
  const [veneers, setVeneers] = useState<CatalogItem[]>([])
  const [sortBy, setSortBy] = useState<'code' | 'name' | 'w' | 'd' | 'h' | 'status'>(
    'code',
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<Product[]>([])
  const [total, setTotal] = useState(0)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [selectedCustomerOption, setSelectedCustomerOption] =
    useState<CustomerFilterOption | null>(ALL_CUSTOMERS_OPTION)

  useEffect(() => {
    void Promise.all([
      catalogService.list('color'),
      catalogService.list('wood'),
      catalogService.list('veneer'),
    ]).then(([c, w, v]) => {
      setColors(c)
      setWoods(w)
      setVeneers(v)
    })
  }, [])

  useEffect(() => {
    void listCustomersApi({ status: 'all' })
      .then((list) => setCustomers(list))
      .catch((err) => console.error('Failed to load customers', err))
  }, [])

  const customerFilterOptions = useMemo<CustomerFilterOption[]>(
    () => [
      ALL_CUSTOMERS_OPTION,
      ...customers.map((c) => ({
        id: c.id,
        code: c.code,
        name: c.name,
        status: c.status,
      })),
    ],
    [customers],
  )

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, status, selectedCustomerOption, pageSize, colorIds, woodIds, veneerIds])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const customerId =
      selectedCustomerOption && selectedCustomerOption.id !== 'all'
        ? selectedCustomerOption.id
        : undefined
    productService
      .list({
        search: debouncedSearch,
        status,
        customerId,
        colorIds,
        woodIds,
        veneerIds,
        sortBy,
        sortDir,
        page,
        pageSize,
      })
      .then((result) => {
        if (cancelled) return
        setItems(result.items)
        setTotal(result.total)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [
    debouncedSearch,
    status,
    selectedCustomerOption,
    colorIds,
    woodIds,
    veneerIds,
    sortBy,
    sortDir,
    page,
    pageSize,
  ])

  function handleExportExcel() {
    const exportData = buildProductExportRows(items, (page - 1) * pageSize)
    const ws = XLSX.utils.json_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'San_pham')
    XLSX.writeFile(wb, 'danh-sach-san-pham.xlsx')
  }

  const columns = useMemo(() => getProductColumns(), [])

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Danh mục', to: '/catalog' },
          { label: 'Sản phẩm' },
        ]}
      />
      <PageHeader
        title="Sản phẩm"
        subtitle="Nhấp vào mã hàng để xem / chỉnh sửa chi tiết."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleExportExcel}>
              <Download className="h-4 w-4" />
              Xuất Excel
            </Button>
            <Button onClick={() => navigate('/products/new')}>
              <Plus className="h-4 w-4" />
              Tạo mã hàng
            </Button>
          </div>
        }
      />

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Tìm kiếm mã hàng, tên hàng..."
        filterValue={status}
        onFilterChange={(v) => setStatus(v as typeof status)}
        filterOptions={[
          { value: 'active', label: 'Đang dùng (OK / Đang cập nhật)' },
          { value: 'all', label: 'Tất cả trạng thái' },
          ...PRODUCT_STATUS_OPTIONS,
        ]}
      />

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
        <div>
          <div className="mb-1 text-xs font-medium text-[var(--color-text-muted)]">
            Khách hàng
          </div>
          <Typeahead<CustomerFilterOption>
            items={customerFilterOptions}
            value={selectedCustomerOption}
            onChange={(opt) => setSelectedCustomerOption(opt)}
            displayValue={(c) =>
              c.id === 'all'
                ? 'Tất cả khách hàng'
                : `${c.code} — ${c.name}${c.status === 'inactive' ? ' (ngừng dùng)' : ''}`
            }
            placeholder="Lọc theo khách hàng..."
            clearable
          />
        </div>
        <MultiSelectChips
          label="Màu"
          options={colors}
          value={colorIds}
          onChange={setColorIds}
          placeholder="Lọc theo màu..."
        />
        <MultiSelectChips
          label="Gỗ"
          options={woods}
          value={woodIds}
          onChange={setWoodIds}
          placeholder="Lọc theo gỗ..."
        />
        <MultiSelectChips
          label="Veneer"
          options={veneers}
          value={veneerIds}
          onChange={setVeneerIds}
          placeholder="Lọc theo veneer..."
        />
      </div>

      <DataTable
        showRowNumbers
        rowNumberOffset={(page - 1) * pageSize}
        columns={columns}
        rows={items}
        rowKey={(row) => row.id}
        loading={loading}
        emptyTitle="Không tìm thấy mã hàng"
        emptyDescription="Thử đổi bộ lọc hoặc tạo mã hàng mới."
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(nextSort, nextDir) => {
          setSortBy(nextSort as typeof sortBy)
          setSortDir(nextDir)
        }}
        onRowClick={(row) => navigate(`/products/${row.id}`)}
        stickyLeftColumnIds={['customer', 'image', 'code', 'name']}
        headerGroups={
          <tr className="border-b border-[var(--color-border)]">
            <th colSpan={8} />
            <th
              colSpan={3}
              className="px-3 py-1.5 text-center text-xs font-semibold tracking-wide text-slate-500 uppercase"
            >
              Kích thước (inch)
            </th>
            <th colSpan={2} />
          </tr>
        }
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  )
}
